import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  createTaskNote,
  fetchBoardTasks,
  fetchTaskNotes,
  type TaskNoteRow,
  type TaskRow,
} from '../api/tasks';
import { formatTaskDate } from '../utils/formatTaskDate';
import { formatDateTimePT } from '../utils/timezone';
import {
  calendarGridDates,
  dayCoversSpan,
  formatCalendarMonthTitle,
  formatWeekRangeTitle,
  isSameLocalDay,
  startOfLocalWeekSunday,
  taskDateSpan,
  taskHue,
  weekDatesFromSundayStart,
} from '../utils/taskCalendarSpan';
import {
  createDefaultProjectBoardColumns,
  fetchProjectBoardLayout,
  loadProjectBoardLayoutLegacy,
  type ProjectBoardColumnDef,
} from '../api/projectBoard';
import { TAG_PILL_COLORS, ROLE_LABELS as TAG_LABELS } from '../constants/taskTags';
import Modal from './Modal';
import { TaskStatusPills } from './TaskStatusPills';
import '../pages/TasksBoard.css';
import './ProjectCalendarTab.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type CalendarViewMode = 'month' | 'week';

const PRIORITY_PILL_COLORS: Record<string, string> = {
  Low: '#73BF4380',
  Medium: '#FFC10780',
  High: '#E48B5280',
  Urgent: '#ef444480',
};

function assigneeDisplay(task: TaskRow): string {
  if (task.assignees && task.assignees.length > 0) {
    return task.assignees.map((a) => a.name).join(', ');
  }
  if (task.assigned_to_name?.trim()) return task.assigned_to_name;
  return '—';
}

/**
 * Prefers the section's real title from the project's shared board layout. Custom
 * sections carry a generated id, which reads as gibberish once prettified.
 */
function boardColumnLabel(id: string | null | undefined, titleById: Map<string, string>): string {
  if (id == null || !String(id).trim()) return '—';
  const key = String(id);
  const title = titleById.get(key);
  if (title) return title;
  return key.replace(/^col-/i, '').replace(/-/g, ' ');
}

export interface ProjectCalendarTabProps {
  projectId: number;
  eventId?: number;
  viewerUserId: number;
}

function previewDescription(text: string | null | undefined): string | null {
  if (text == null || !String(text).trim()) return null;
  const s = String(text).trim();
  if (s.length <= 900) return s;
  return `${s.slice(0, 900)}…`;
}

function fmtNoteDateTime(iso: string): string {
  if (!iso) return '—';
  return formatDateTimePT(iso, { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', second: undefined, hour12: true });
}

const ProjectCalendarTab: React.FC<ProjectCalendarTabProps> = ({ projectId, eventId, viewerUserId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calendarTaskPreview, setCalendarTaskPreview] = useState<TaskRow | null>(null);
  const [previewNotes, setPreviewNotes] = useState<TaskNoteRow[]>([]);
  const [previewNotesLoading, setPreviewNotesLoading] = useState(false);
  const [previewNotesLoadError, setPreviewNotesLoadError] = useState('');
  const [previewNoteDraft, setPreviewNoteDraft] = useState('');
  const [previewNoteSaving, setPreviewNoteSaving] = useState(false);
  const [previewNoteFormError, setPreviewNoteFormError] = useState('');
  const [previewNoteFormSuccess, setPreviewNoteFormSuccess] = useState('');
  const [viewMode, setViewMode] = useState<CalendarViewMode>('month');
  const [boardColumnDefs, setBoardColumnDefs] = useState<ProjectBoardColumnDef[]>(() =>
    createDefaultProjectBoardColumns()
  );
  /** Month view: first of month. Week view: local Sunday 00:00 of the displayed week. */
  const [viewAnchor, setViewAnchor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  useEffect(() => {
    let cancelled = false;
    if (!Number.isInteger(projectId) || projectId < 1 || !Number.isInteger(viewerUserId) || viewerUserId < 1) return;
    fetchProjectBoardLayout(projectId, viewerUserId)
      .then((layout) => {
        if (cancelled) return;
        setBoardColumnDefs(
          layout?.columns ?? loadProjectBoardLayoutLegacy(projectId)?.columns ?? createDefaultProjectBoardColumns()
        );
      })
      .catch(() => {
        /* Fall back to labelling from the column id. */
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, viewerUserId]);

  const boardColumnTitleById = useMemo(
    () => new Map(boardColumnDefs.map((c) => [c.id, c.title])),
    [boardColumnDefs]
  );

  const goToMonthView = useCallback(() => {
    setViewMode('month');
    setViewAnchor((a) => new Date(a.getFullYear(), a.getMonth(), 1));
  }, []);

  const goToWeekView = useCallback(() => {
    setViewMode('week');
    setViewAnchor((a) => startOfLocalWeekSunday(a));
  }, []);

  const reload = useCallback(async () => {
    if (!Number.isInteger(projectId) || projectId < 1 || !Number.isInteger(viewerUserId) || viewerUserId < 1) {
      setTasks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const list = await fetchBoardTasks(viewerUserId, { projectId, eventId });
      setTasks(list);
    } catch {
      setError('Failed to load tasks.');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, eventId, viewerUserId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const task = calendarTaskPreview;
    if (!task) {
      setPreviewNotes([]);
      setPreviewNotesLoading(false);
      setPreviewNotesLoadError('');
      setPreviewNoteDraft('');
      setPreviewNoteFormError('');
      setPreviewNoteFormSuccess('');
      return;
    }
    setPreviewNoteDraft('');
    setPreviewNoteFormError('');
    setPreviewNoteFormSuccess('');
    let cancelled = false;
    setPreviewNotesLoading(true);
    setPreviewNotesLoadError('');
    void fetchTaskNotes(task.id)
      .then((list) => {
        if (!cancelled) setPreviewNotes(list);
      })
      .catch((e) => {
        if (!cancelled) {
          setPreviewNotes([]);
          setPreviewNotesLoadError(e instanceof Error ? e.message : 'Failed to load notes.');
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewNotesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [calendarTaskPreview?.id]);

  const previewNotesNewestFirst = useMemo(() => [...previewNotes].reverse(), [previewNotes]);

  const submitPreviewNote = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const task = calendarTaskPreview;
      if (!task || !Number.isInteger(viewerUserId) || viewerUserId < 1) return;
      const trimmed = previewNoteDraft.trim();
      if (!trimmed) return;
      setPreviewNoteSaving(true);
      setPreviewNoteFormError('');
      setPreviewNoteFormSuccess('');
      try {
        const created = await createTaskNote(task.id, { note: trimmed, user_id: viewerUserId });
        setPreviewNotes((prev) => [...prev, created]);
        setPreviewNoteDraft('');
        setPreviewNoteFormSuccess('Note added. Assigner and assignees were notified.');
        window.setTimeout(() => setPreviewNoteFormSuccess(''), 5000);
      } catch (err) {
        setPreviewNoteFormError(err instanceof Error ? err.message : 'Failed to add note.');
      } finally {
        setPreviewNoteSaving(false);
      }
    },
    [calendarTaskPreview, previewNoteDraft, viewerUserId]
  );

  const normalizedTasks = useMemo((): TaskRow[] => {
    return tasks
      .filter((t) => t.status !== 'Void')
      .map((t) => ({
        ...t,
        project_id: t.project_id ?? projectId,
      }));
  }, [tasks, projectId]);

  const year = viewAnchor.getFullYear();
  const month0 = viewAnchor.getMonth();

  const cells = useMemo(() => {
    if (viewMode === 'week') return weekDatesFromSundayStart(viewAnchor);
    return calendarGridDates(year, month0);
  }, [viewMode, viewAnchor, year, month0]);

  const toolbarTitle =
    viewMode === 'week' ? formatWeekRangeTitle(viewAnchor) : formatCalendarMonthTitle(year, month0);

  const maxTasksPerDayCell = viewMode === 'week' ? 12 : 6;

  const tasksWithSpan = useMemo(() => {
    const out: { task: TaskRow; span: { start: Date; end: Date } }[] = [];
    for (const task of normalizedTasks) {
      const span = taskDateSpan(task);
      if (span) out.push({ task, span });
    }
    return out;
  }, [normalizedTasks]);

  const sortedForTable = useMemo(() => {
    return [...normalizedTasks].sort((a, b) => {
      const sa = taskDateSpan(a)?.start.getTime() ?? Number.MAX_SAFE_INTEGER;
      const sb = taskDateSpan(b)?.start.getTime() ?? Number.MAX_SAFE_INTEGER;
      if (sa !== sb) return sa - sb;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [normalizedTasks]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!Number.isInteger(viewerUserId) || viewerUserId < 1) {
    return <p className="project-cal-tab-error">Sign in to view the project calendar.</p>;
  }

  return (
    <div className="project-cal-tab">
      {error ? (
        <p className="project-cal-tab-error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? <p className="project-cal-tab-loading">Loading schedule…</p> : null}

      {!loading && !error ? (
        <>
          <div className="project-cal-tab-toolbar">
            <button
              type="button"
              className="project-cal-tab-nav-btn"
              onClick={() =>
                setViewAnchor((prev) =>
                  viewMode === 'month'
                    ? new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
                    : (() => {
                        const d = new Date(prev);
                        d.setDate(d.getDate() - 7);
                        return d;
                      })()
                )
              }
              aria-label={viewMode === 'month' ? 'Previous month' : 'Previous week'}
            >
              ‹
            </button>
            <h2 className="project-cal-tab-toolbar-title">{toolbarTitle}</h2>
            <button
              type="button"
              className="project-cal-tab-nav-btn"
              onClick={() =>
                setViewAnchor((prev) =>
                  viewMode === 'month'
                    ? new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
                    : (() => {
                        const d = new Date(prev);
                        d.setDate(d.getDate() + 7);
                        return d;
                      })()
                )
              }
              aria-label={viewMode === 'month' ? 'Next month' : 'Next week'}
            >
              ›
            </button>
            <div
              className="project-cal-tab-view-toggle"
              role="group"
              aria-label="Calendar view"
            >
              <button
                type="button"
                className={
                  viewMode === 'month'
                    ? 'project-cal-tab-view-toggle-btn project-cal-tab-view-toggle-btn--active'
                    : 'project-cal-tab-view-toggle-btn'
                }
                onClick={goToMonthView}
                aria-pressed={viewMode === 'month'}
              >
                Month
              </button>
              <button
                type="button"
                className={
                  viewMode === 'week'
                    ? 'project-cal-tab-view-toggle-btn project-cal-tab-view-toggle-btn--active'
                    : 'project-cal-tab-view-toggle-btn'
                }
                onClick={goToWeekView}
                aria-pressed={viewMode === 'week'}
              >
                Week
              </button>
            </div>
            <button
              type="button"
              className="project-cal-tab-today-btn"
              onClick={() => {
                const n = new Date();
                if (viewMode === 'month') {
                  setViewAnchor(new Date(n.getFullYear(), n.getMonth(), 1));
                } else {
                  setViewAnchor(startOfLocalWeekSunday(n));
                }
              }}
            >
              {viewMode === 'month' ? 'This month' : 'This week'}
            </button>
          </div>

          <div className={viewMode === 'week' ? 'project-cal-expanded project-cal-expanded--week' : 'project-cal-expanded'}>
            <p className="project-cal-expanded-hint">
              Click a task to see details. Days list tasks that overlap from <strong>start</strong> through{' '}
              <strong>due</strong> (inclusive).
            </p>
            <div className="project-cal-expanded-weekdays">
              {WEEKDAYS.map((w) => (
                <div key={w} className="project-cal-expanded-weekday">
                  {w}
                </div>
              ))}
            </div>
            <div className="project-cal-expanded-cells">
              {cells.map((day) => {
                const inMonth = viewMode === 'month' && day.getMonth() === month0;
                const isToday = isSameLocalDay(day, today);
                const covering = tasksWithSpan.filter(({ span }) => dayCoversSpan(day, span));
                return (
                  <div
                    key={day.getTime()}
                    className={[
                      'project-cal-expanded-cell',
                      !inMonth ? 'project-cal-expanded-cell--outside' : '',
                      isToday ? 'project-cal-expanded-cell--today' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className="project-cal-expanded-daynum">{day.getDate()}</div>
                    <div className="project-cal-expanded-tasks">
                      {covering.slice(0, maxTasksPerDayCell).map(({ task }) => (
                        <button
                          key={task.id}
                          type="button"
                          className="project-cal-expanded-task"
                          style={{ borderLeftColor: `hsl(${taskHue(task.id)}, 58%, 45%)` }}
                          title={`#${task.id} — ${formatTaskDate(task.start_date)} → ${formatTaskDate(task.due_date)}`}
                          onClick={() => setCalendarTaskPreview(task)}
                        >
                          {task.name}
                        </button>
                      ))}
                      {covering.length > maxTasksPerDayCell ? (
                        <span className="project-cal-expanded-more">
                          +{covering.length - maxTasksPerDayCell} more
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Modal
            isOpen={calendarTaskPreview != null}
            onClose={() => setCalendarTaskPreview(null)}
            title={
              calendarTaskPreview
                ? `Task #${calendarTaskPreview.id}`
                : 'Task'
            }
            wide
          >
            {calendarTaskPreview ? (
              <div className="modal-body project-cal-task-preview-modal-body">
                <div className="project-cal-task-preview">
                  <div
                    className="project-cal-task-preview-accent"
                    style={{
                      backgroundColor: `hsl(${taskHue(calendarTaskPreview.id)}, 58%, 48%)`,
                    }}
                    aria-hidden="true"
                  />
                  <div className="project-cal-task-preview-row">
                    <span className="project-cal-task-preview-label">Name</span>
                    <span className="project-cal-task-preview-value">{calendarTaskPreview.name}</span>
                  </div>
                <div className="project-cal-task-preview-row">
                  <span className="project-cal-task-preview-label">Status</span>
                  <div className="project-cal-task-preview-value">
                    <TaskStatusPills task={calendarTaskPreview} />
                  </div>
                </div>
                <div className="project-cal-task-preview-row">
                  <span className="project-cal-task-preview-label">Priority</span>
                  <span className="project-cal-task-preview-value">
                    {calendarTaskPreview.priority ? (
                      <span
                        className="tasks-tag-pill"
                        style={{
                          backgroundColor: PRIORITY_PILL_COLORS[calendarTaskPreview.priority] ?? '#e5e7eb',
                        }}
                      >
                        {calendarTaskPreview.priority}
                      </span>
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
                <div className="project-cal-task-preview-row">
                  <span className="project-cal-task-preview-label">Board column</span>
                  <span className="project-cal-task-preview-value">
                    {boardColumnLabel(calendarTaskPreview.project_board_column_id, boardColumnTitleById)}
                  </span>
                </div>
                <div className="project-cal-task-preview-row">
                  <span className="project-cal-task-preview-label">Start date</span>
                  <span className="project-cal-task-preview-value">{formatTaskDate(calendarTaskPreview.start_date)}</span>
                </div>
                <div className="project-cal-task-preview-row">
                  <span className="project-cal-task-preview-label">Due date</span>
                  <span className="project-cal-task-preview-value">{formatTaskDate(calendarTaskPreview.due_date)}</span>
                </div>
                <div className="project-cal-task-preview-row">
                  <span className="project-cal-task-preview-label">Assigned to</span>
                  <span className="project-cal-task-preview-value">{assigneeDisplay(calendarTaskPreview)}</span>
                </div>
                <div className="project-cal-task-preview-row">
                  <span className="project-cal-task-preview-label">Assigned by</span>
                  <span className="project-cal-task-preview-value">
                    {calendarTaskPreview.assigner_name?.trim() || '—'}
                  </span>
                </div>
                <div className="project-cal-task-preview-row">
                  <span className="project-cal-task-preview-label">Client</span>
                  <span className="project-cal-task-preview-value">
                    {calendarTaskPreview.client_company?.trim() || '—'}
                  </span>
                </div>
                {(calendarTaskPreview.tags?.length ?? 0) > 0 ? (
                  <div className="project-cal-task-preview-row">
                    <span className="project-cal-task-preview-label">Roles / tags</span>
                    <div className="project-cal-task-preview-value">
                      {(calendarTaskPreview.tags ?? []).map((tag) => (
                        <span
                          key={tag}
                          className="tasks-tag-pill"
                          style={{ backgroundColor: TAG_PILL_COLORS[tag] ?? '#8A8F93' }}
                        >
                          {TAG_LABELS[tag] ?? tag.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
                {calendarTaskPreview.depends_on_task_name ? (
                  <div className="project-cal-task-preview-row">
                    <span className="project-cal-task-preview-label">Depends on</span>
                    <span className="project-cal-task-preview-value">{calendarTaskPreview.depends_on_task_name}</span>
                  </div>
                ) : null}
                {calendarTaskPreview.progress_percent != null &&
                Number.isFinite(Number(calendarTaskPreview.progress_percent)) ? (
                  <div className="project-cal-task-preview-row">
                    <span className="project-cal-task-preview-label">Progress</span>
                    <span className="project-cal-task-preview-value">{calendarTaskPreview.progress_percent}%</span>
                  </div>
                ) : null}
                {previewDescription(calendarTaskPreview.description) ? (
                  <div className="project-cal-task-preview-row">
                    <span className="project-cal-task-preview-label">Description</span>
                    <p className="project-cal-task-preview-desc">{previewDescription(calendarTaskPreview.description)}</p>
                  </div>
                ) : null}

                <div className="project-cal-task-preview-notes" aria-labelledby="project-cal-preview-notes-heading">
                  <h4 className="project-cal-task-preview-notes-title" id="project-cal-preview-notes-heading">
                    Notes
                  </h4>
                  {previewNotesLoading ? (
                    <p className="project-cal-task-preview-notes-hint">Loading notes…</p>
                  ) : null}
                  {previewNotesLoadError ? (
                    <p className="project-cal-task-preview-notes-error" role="alert">
                      {previewNotesLoadError}
                    </p>
                  ) : null}
                  {!previewNotesLoading && !previewNotesLoadError && previewNotesNewestFirst.length > 0 ? (
                    <ul className="project-cal-task-preview-notes-list">
                      {previewNotesNewestFirst.map((n) => (
                        <li key={n.id} className="project-cal-task-preview-notes-item">
                          <div className="project-cal-task-preview-note-meta">
                            <span>{n.submitted_by?.trim() || '—'}</span>
                            <span aria-hidden="true"> · </span>
                            <time dateTime={n.created_at}>{fmtNoteDateTime(n.created_at)}</time>
                          </div>
                          <p className="project-cal-task-preview-note-text">{n.note}</p>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {!previewNotesLoading && !previewNotesLoadError && previewNotes.length === 0 ? (
                    <p className="project-cal-task-preview-notes-hint">No notes yet.</p>
                  ) : null}

                  <form className="project-cal-task-preview-note-form" onSubmit={submitPreviewNote}>
                    <label htmlFor="project-cal-preview-note" className="project-cal-task-preview-label">
                      Add a note
                    </label>
                    <textarea
                      id="project-cal-preview-note"
                      className="project-cal-task-preview-note-input form-input"
                      rows={3}
                      value={previewNoteDraft}
                      onChange={(e) => setPreviewNoteDraft(e.target.value)}
                      placeholder="Write an update for assigner and assignees…"
                      disabled={previewNoteSaving}
                      maxLength={8000}
                    />
                    <div className="project-cal-task-preview-note-form-footer">
                      <button
                        type="submit"
                        className="project-cal-task-preview-note-submit"
                        disabled={previewNoteSaving || !previewNoteDraft.trim()}
                      >
                        {previewNoteSaving ? 'Adding…' : 'Add note'}
                      </button>
                    </div>
                  </form>
                  {previewNoteFormSuccess ? (
                    <p className="project-cal-task-preview-notes-success" role="status">
                      {previewNoteFormSuccess}
                    </p>
                  ) : null}
                  {previewNoteFormError ? (
                    <p className="project-cal-task-preview-notes-error" role="alert">
                      {previewNoteFormError}
                    </p>
                  ) : null}
                </div>

                <div className="project-cal-task-preview-actions">
                  <button
                    type="button"
                    className="project-cal-task-preview-primary"
                    onClick={() => {
                      const id = calendarTaskPreview.id;
                      setCalendarTaskPreview(null);
                      navigate(`/tasks/${id}`, { state: { from: location.pathname + location.search } });
                    }}
                  >
                    View full task
                  </button>
                  <button
                    type="button"
                    className="project-cal-task-preview-secondary"
                    onClick={() => setCalendarTaskPreview(null)}
                  >
                    Close
                  </button>
                </div>
                </div>
              </div>
            ) : null}
          </Modal>

          <section className="project-cal-table-section" aria-labelledby="project-cal-table-heading">
            <h3 className="project-cal-table-title" id="project-cal-table-heading">
              All tasks — schedule &amp; assignment detail
            </h3>
            <div className="project-cal-table-wrap">
              <table className="project-cal-table">
                <thead>
                  <tr>
                    <th scope="col">
                      <abbr title="Task color on calendar">Key</abbr>
                    </th>
                    <th scope="col">Task</th>
                    <th scope="col">Status</th>
                    <th scope="col">Board column</th>
                    <th scope="col">Start</th>
                    <th scope="col">Due</th>
                    <th scope="col">Assigned to</th>
                    <th scope="col">Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedForTable.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="project-cal-table-muted">
                        No tasks on this project board yet.
                      </td>
                    </tr>
                  ) : (
                    sortedForTable.map((task) => {
                      const span = taskDateSpan(task);
                      return (
                        <tr
                          key={task.id}
                          onClick={() =>
                            navigate(`/tasks/${task.id}`, { state: { from: location.pathname + location.search } })
                          }
                        >
                          <td>
                            <span
                              className="project-cal-table-swatch"
                              style={{
                                backgroundColor: span ? `hsl(${taskHue(task.id)}, 58%, 52%)` : '#e2e8f0',
                              }}
                              aria-hidden="true"
                            />
                          </td>
                          <td>
                            <span className="project-cal-table-task-name">{task.name}</span>
                            <div className="project-cal-table-muted">#{task.id}</div>
                          </td>
                          <td>
                            <TaskStatusPills task={task} />
                          </td>
                          <td>{boardColumnLabel(task.project_board_column_id, boardColumnTitleById)}</td>
                          <td>{formatTaskDate(task.start_date)}</td>
                          <td>{formatTaskDate(task.due_date)}</td>
                          <td>{assigneeDisplay(task)}</td>
                          <td>
                            {task.priority ? (
                              <span
                                className="tasks-tag-pill"
                                style={{ backgroundColor: PRIORITY_PILL_COLORS[task.priority] ?? '#e5e7eb' }}
                              >
                                {task.priority}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
};

export default ProjectCalendarTab;
